import { useFetch, useSubmit } from "@hyper-fetch/react";
import { FileX2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { client } from "@/api/client";
import { sdk } from "@/api/sdk";
import { DialogShell } from "@/components/layouts/shells/dialog-shell";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { formatDate } from "@/features/payments/utils/format";
import { m } from "@/paraglide/messages.js";
import { localizeHref } from "@/paraglide/runtime.js";

/** What a purchase says about withdrawing: the person can, did, or it does not apply. */
export type BuyerWithdrawal =
  | { state: "open"; until: string | null }
  | { state: "requested"; requestedAt: string }
  | { state: "none" };

export type WithdrawalContract = { orderId: string } | { clientSubscriptionId: string };

function ConfirmDialog({
  contract,
  title,
  seller,
  price,
  onClose,
}: {
  contract: WithdrawalContract;
  title: string;
  seller: string;
  price: string;
  onClose: () => void;
}) {
  const { data: me } = useFetch(sdk.users.me.$get);
  const fromOrder = useSubmit(sdk.me.payments.orders.$orderId.withdrawal.$post);
  const fromSubscription = useSubmit(sdk.me.payments.subscriptions.$clientSubscriptionId.withdrawal.$post);
  const submitting = fromOrder.submitting || fromSubscription.submitting;

  const confirm = async () => {
    const { data, error } = await ("orderId" in contract
      ? fromOrder.submit({ params: { orderId: contract.orderId } })
      : fromSubscription.submit({ params: { clientSubscriptionId: contract.clientSubscriptionId } }));
    if (error || !data) {
      toast.error(error?.message || m.withdrawal_failed());
      return;
    }
    client.cache.invalidate(/\/me\/payments/);
    toast.success(m.withdrawal_sent({ email: data.email }));
    onClose();
  };

  return (
    <DialogShell
      open
      onOpenChange={(open) => !open && !submitting && onClose()}
      icon={FileX2}
      title={m.withdrawal_dialog_title()}
      className="sm:max-w-md"
      footer={
        <div className="grid grid-cols-2 gap-2 sm:flex sm:justify-end">
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
            {m.withdrawal_keep()}
          </Button>
          <Button type="button" onClick={() => void confirm()} disabled={submitting}>
            {submitting ? <Spinner /> : null}
            {m.withdrawal_confirm()}
          </Button>
        </div>
      }
    >
      <div className="grid gap-3 text-sm">
        <p>{m.withdrawal_dialog_body({ seller })}</p>
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1">
          <dt className="text-muted-foreground">{m.withdrawal_field_purchase()}</dt>
          <dd className="font-medium">
            {title}, <span className="tabular-nums">{price}</span>
          </dd>
          {me?.user.email ? (
            <>
              <dt className="text-muted-foreground">{m.withdrawal_field_email()}</dt>
              <dd className="font-medium break-words">{me.user.email}</dd>
            </>
          ) : null}
        </dl>
        <p className="text-muted-foreground">{m.withdrawal_dialog_next({ seller })}</p>
        <a href={localizeHref("/withdrawal")} target="_blank" rel="noreferrer" className="text-muted-foreground underline underline-offset-2">
          {m.withdrawal_learn()}
        </a>
      </div>
    </DialogShell>
  );
}

/**
 * The online withdrawal function on one purchase: a button labelled as the law asks, while the right
 * applies, then a confirm step. Once sent, it says so for good. Nothing shows where the right does
 * not apply, such as a session with a date.
 */
export function WithdrawalAction({
  withdrawal,
  contract,
  title,
  seller,
  price,
}: {
  withdrawal: BuyerWithdrawal;
  contract: WithdrawalContract;
  title: string;
  seller: string;
  price: string;
}) {
  const [confirming, setConfirming] = useState(false);

  if (withdrawal.state === "none") return null;
  if (withdrawal.state === "requested") {
    return (
      <span className="text-xs text-muted-foreground">{m.withdrawal_requested({ date: formatDate(withdrawal.requestedAt) })}</span>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <Button type="button" variant="outline" size="sm" onClick={() => setConfirming(true)}>
        {m.withdrawal_action()}
      </Button>
      <span className="text-xs text-muted-foreground">
        {withdrawal.until ? m.withdrawal_until({ date: formatDate(withdrawal.until) }) : m.withdrawal_until_delivery()}
      </span>
      {confirming ? (
        <ConfirmDialog contract={contract} title={title} seller={seller} price={price} onClose={() => setConfirming(false)} />
      ) : null}
    </div>
  );
}
